import { useState, type ReactNode } from "react";
import { Button } from "@/app/components/system/Button";
import { XIcon } from "@/app/components/system/Icons";

interface DeleteConfirmBannerProps {
  /** Visible label on the resting "Delete …" trigger. */
  triggerLabel: ReactNode;
  /** Confirmation prompt shown after the trigger is pressed. */
  question: ReactNode;
  /** Caller's destructive action; awaited before the banner re-renders. */
  onConfirm: () => void | Promise<void>;
  /** Disable the trigger when destructive action isn't allowed yet. */
  disabled?: boolean;
  /** Optional helper text rendered below the trigger when disabled. */
  helpText?: ReactNode;
}

/** Dashed-border danger banner: trigger button → "Are you sure?" prompt with Keep/Delete. */
export function DeleteConfirmBanner({
  triggerLabel,
  question,
  onConfirm,
  disabled = false,
  helpText,
}: DeleteConfirmBannerProps) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      className="p-3.5 border border-dashed rounded-2xl"
      style={{ borderColor: "oklch(0.78 0.07 28)", background: "oklch(0.97 0.02 28 / 0.4)" }}
    >
      {!confirming ? (
        <Button variant="danger" onClick={() => setConfirming(true)} disabled={disabled}>
          <XIcon size={14} /> {triggerLabel}
        </Button>
      ) : (
        <div>
          <p className="font-sans text-[13px] text-ink m-0 mb-3 leading-[1.5]">{question}</p>
          <div className="flex gap-2 [&>button]:flex-1">
            <Button variant="secondary" onClick={() => setConfirming(false)}>Keep</Button>
            <Button
              variant="danger"
              size="sm"
              className="justify-center"
              onClick={onConfirm}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
      {disabled && !confirming && helpText ? (
        <p className="font-sans text-xs text-ink-3 m-0 mt-2 leading-snug">{helpText}</p>
      ) : null}
    </div>
  );
}
